import { GA4EcommercePayloadItem, EventValues } from '@sial/common-utils'
import { searchResultsRoute } from '@src/routes'
import {
  ProductSearchType,
  determineQueryFocus,
  getValidSearch,
  formatCasNumberSearchTerm,
} from '@utils/searchUtils'

import { ProductAliasPayload } from './types'
import {
  CarouselProduct,
  CarouselProductTypes,
} from '@src/components/DynamicProductCarousel/DynamicProductCardTypes'
import subscriptionFrequencyMessages from '@src/utils/MessageSets/defineSubscriptionFrequencyMessages'

export const buildProductAliasPayload = ({
  action,
  detail,
  focus,
  productId,
  productBrand,
}: {
  action: string
  detail?: string | undefined | null
  focus: string
  productId?: string
  productBrand?: string
}): ProductAliasPayload => {
  let type
  let term
  switch (action) {
    case 'ec number':
      type = ProductSearchType.EgecNumber
      break
    case 'mdl number':
      type = ProductSearchType.MdlNumber
      break
    case 'cas number':
      type = ProductSearchType.CasNumber
      term = formatCasNumberSearchTerm(detail)
      break
    default:
  }
  return {
    action: action + ' click',
    detail: detail,
    component: 'body',
    elementType: 'link',
    elementText: detail,
    linkUrl: `${searchResultsRoute.searchResults(
      term || detail || undefined
    )}${getValidSearch({
      term: term?.toLowerCase() || detail?.toLowerCase(),
      focus: determineQueryFocus(focus),
      type,
    })}`,
    productId,
    productBrand,
  }
}

export const makeListIdAndNameFields = (
  promoType: CarouselProductTypes | null = null,
  itemListingIdOverride: string | null = null,
  itemListingNameOverride: string | null = null
): { list_id: string; list_name: string } => {
  const resolvedItemId =
    itemListingIdOverride?.toLowerCase() ||
    promoType ||
    EventValues.NotAvailable

  const resolvedItemListName =
    itemListingNameOverride?.toLocaleLowerCase() ||
    itemListingIdOverride?.toLowerCase() ||
    promoType ||
    EventValues.NotAvailable

  return { list_id: resolvedItemId, list_name: resolvedItemListName }
}

/**
 * BUILD PROMO OBJECTS FOR CAROUSELS:
 * Could be used elsewhere for product data structures that use the gaProductCode alongside product data.
 * Carousels is the original primary purpose.
 *
 * NOTE: promoType fills in the item_list_id and item_list_name when those values are not supplied.
 *
 * NOTE: item_list_id fills in item_list_name if 'id' is supplied but name is absent.
 *
 * @param rawGaProductCode String BE gives with pipe separated promotion data for GA.
 * @param pickedProduct CarouselProduct type with data on product viewed or clicked in carousel.
 * @param position number position of where the item was in the carousel left to right.
 * @param promoType (optional) CarouselProductTypes enum that flags the type of carousel that the promo is coming from.
 * @param itemListingIdOverride (optional) string override of page type carousel was displayed on.
 * @param itemListingNameOverride (optional) string same as listingLocationOverride but extended detail summary space.
 * @returns GA4EcommercePayloadItem
 */
export const buildGaPromoObject = (
  rawGaProductCode: string | null | undefined,
  pickedProduct: CarouselProduct,
  position: number = 1,
  promoType: CarouselProductTypes | null = null,
  itemListingIdOverride: string | null = null,
  itemListingNameOverride: string | null = null
): GA4EcommercePayloadItem => {
  const gaProductCode = rawGaProductCode?.toLowerCase()?.split('|') || []

  const itemList = makeListIdAndNameFields(
    promoType,
    itemListingIdOverride,
    itemListingNameOverride
  )

  const ga4PromoObj: GA4EcommercePayloadItem = {
    promotion_id: gaProductCode[0] || EventValues.NotAvailable,
    promotion_name: gaProductCode[1] || EventValues.NotAvailable,
    creative_name: gaProductCode[2] || EventValues.NotAvailable,
    index: position,
    item_id: pickedProduct.productNumber.toLowerCase(),
    item_brand:
      pickedProduct.brand?.key?.toLowerCase() || EventValues.NotAvailable,
    item_list_id: itemList.list_id,
    item_list_name: itemList.list_name,
  }

  return ga4PromoObj
}

export const calculateDiscountPercentage = (
  listPrice,
  discountedPrice
): number | undefined => {
  return (listPrice ?? 0) > (discountedPrice ?? 0)
    ? Math.round(
        (((listPrice ?? 0) - (discountedPrice ?? 0)) / (listPrice ?? 1)) * 100
      )
    : undefined
}
export const getSubscriptionFrequencyLabel = (
  key: string | null | undefined
): string => {
  if (!key) return 'one time order'
  return subscriptionFrequencyMessages[key]?.value ?? 'one time order'
}
